import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  LEASE_LABEL, KIND_LABEL, leasePill, pill, unitState, LINK, MUTED, LABEL, TEXTAREA, money, fmtYmd,
  BatteryBadges, UnitForm, makeModel, BLANK_UNIT, readJson, JSON_HEADERS,
} from '../lib/equipment';
import { PosInbox } from '../components/PosReports'; // @@POS_REPORTS_v104@@

// @@EQUIPMENT_LEASES_v87@@ HQ › Equipment — leases, inventory, standard terms.
// Reads: admin/manager/staff. Writes: admin/manager (the server re-checks).
const FILTERS = [['open', 'Open'], ['sent', 'Awaiting client'], ['active', 'Active'], ['draft', 'Drafts'], ['returned', 'Returned'], ['cancelled', 'Cancelled'], ['', 'All']];

export default function Equipment() {
  const { user } = useAuth();
  const canManage = !!user && ['admin', 'manager'].includes(user.role);
  const [params, setParams] = useSearchParams();
  const tab = ['leases', 'pos', 'inventory', 'terms'].includes(params.get('tab')) /* @@POS_REPORTS_v104@@ +pos */ ? params.get('tab') : 'leases';
  const setTab = (t) => setParams(t === 'leases' ? {} : { tab: t }, { replace: true });
  const [msg, setMsg] = useState('');

  const tabBtn = (key, label) => (
    <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => { setMsg(''); setTab(key); }}
      style={{ height: 42, padding: '0 14px', background: 'none', border: 0, borderBottom: tab === key ? '2px solid #31b44b' : '2px solid transparent', color: tab === key ? 'var(--hq-txt)' : 'var(--hq-muted)', font: 'inherit', fontSize: 14, cursor: 'pointer' }}>
      {label}
    </button>
  );

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Equipment</h1>
        <p>Card readers and other gear we lease to clients. Clients accept the lease terms in their portal.</p>
      </div>
      {msg && <p className="hq-msg" style={{ marginTop: -12, marginBottom: 16 }}>{msg}</p>}
      <div role="tablist" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, borderBottom: '1px solid var(--hq-line)', marginBottom: 20 }}>
        {tabBtn('leases', 'Leases')}
        {tabBtn('pos', 'POS sales')}
        {tabBtn('inventory', 'Inventory')}
        {tabBtn('terms', 'Standard terms')}
      </div>
      {tab === 'leases' && <LeasesPanel canManage={canManage} onMsg={setMsg} />}
      {tab === 'pos' && <PosInbox canManage={canManage} />}
      {tab === 'inventory' && <InventoryPanel canManage={canManage} onMsg={setMsg} />}
      {tab === 'terms' && <TermsPanel canManage={canManage} onMsg={setMsg} />}
    </div>
  );
}

function LeasesPanel({ canManage, onMsg }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('open');
  const [leases, setLeases] = useState(null);
  const [clients, setClients] = useState([]);
  const [draft, setDraft] = useState({ client_id: '', title: 'Credit Card POS Rental' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let on = true;
    setLeases(null);
    apiFetch('/api/equipment/leases' + (filter ? '?status=' + filter : ''))
      .then(async (r) => { const d = await readJson(r); if (!on) return; if (r.ok) setLeases(d.leases || []); else { setLeases([]); onMsg(d.error || 'Could not load leases.'); } })
      .catch(() => on && setLeases([]));
    return () => { on = false; };
  }, [filter, onMsg]);
  useEffect(() => {
    if (!canManage) return;
    apiFetch('/api/equipment/clients').then(readJson).then((d) => setClients(d.clients || [])).catch(() => {});
  }, [canManage]);

  async function create(e) {
    e.preventDefault();
    if (!draft.client_id) return;
    setBusy(true);
    const r = await apiFetch('/api/equipment/leases', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(draft) });
    const d = await readJson(r);
    setBusy(false);
    if (r.ok) navigate('/equipment/leases/' + d.lease.id); else onMsg(d.error || 'Could not create the lease.');
  }
  const picked = clients.find((c) => c.id === draft.client_id);

  return (
    <>
      {canManage && (
        <form className="hq-panel" style={{ marginBottom: 16 }} onSubmit={create}>
          <div className="hq-panel-head"><h2>New lease</h2></div>
          <div style={{ padding: 20, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ flex: '1 1 240px' }}><span style={LABEL}>Client</span>
              <select className="hq-select" style={{ width: '100%' }} value={draft.client_id} onChange={(e) => setDraft({ ...draft, client_id: e.target.value })}>
                <option value="">Choose a client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label style={{ flex: '1 1 240px' }}><span style={LABEL}>Subject</span>
              <input className="hq-input" style={{ width: '100%', boxSizing: 'border-box' }} value={draft.title} maxLength={120} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            </label>
            <button className="hq-btn" type="submit" disabled={!draft.client_id || busy}>Create draft</button>
          </div>
          {picked && picked.portal_users === 0 && (
            <p style={{ ...MUTED, color: '#e8c15a', padding: '0 20px 16px', margin: 0 }}>
              {picked.name} has no client-portal login yet, so they can’t accept a lease until one of their contacts is invited to the portal.
            </p>
          )}
        </form>
      )}
      <div className="hq-panel">
        <div className="hq-panel-head" style={{ flexWrap: 'wrap', gap: 10 }}>
          <select className="hq-select" aria-label="Filter leases" value={filter} onChange={(e) => setFilter(e.target.value)}>
            {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="hq-table" style={{ minWidth: 760 }}>
            <thead><tr><th>Lease</th><th>Client</th><th>Subject / event</th><th>Rental period</th><th>Units</th><th>Status</th></tr></thead>
            <tbody>
              {(leases || []).map((l) => (
                <tr key={l.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/equipment/leases/' + l.id)}>
                  <td style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }}>
                    <Link to={'/equipment/leases/' + l.id} style={LINK} onClick={(e) => e.stopPropagation()}>{l.number}</Link></td>
                  <td>{l.client_name}</td>
                  <td>{l.title}{l.event_name && <div style={MUTED}>{l.event_name}</div>}</td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{fmtYmd(l.start_date)} – {fmtYmd(l.end_date)}
                    {l.overdue && <div><span style={pill('red')}>Past end date</span></div>}</td>
                  <td>{l.status === 'active' ? `${l.units_out} out / ${l.units}` : l.units}</td>
                  <td><span style={leasePill(l.status)}>{LEASE_LABEL[l.status] || l.status}</span></td>
                </tr>
              ))}
              {leases && leases.length === 0 && <tr><td className="hq-empty" colSpan={6}>No leases here yet.</td></tr>}
              {leases === null && <tr><td className="hq-empty" colSpan={6}>Loading…</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function InventoryPanel({ canManage, onMsg }) {
  const navigate = useNavigate();
  const [units, setUnits] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK_UNIT);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  async function load() {
    const r = await apiFetch('/api/equipment/units');
    const d = await readJson(r);
    if (r.ok) setUnits(d.units || []); else { setUnits([]); onMsg(d.error || 'Could not load inventory.'); }
  }
  useEffect(() => { load(); }, []);

  async function add(e) {
    e.preventDefault();
    setBusy(true);
    const r = await apiFetch('/api/equipment/units', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(form) });
    const d = await readJson(r);
    setBusy(false);
    if (!r.ok) { onMsg(d.error || 'Could not add the unit.'); return; }
    onMsg(`Added ${d.unit.asset_tag}.`);
    setForm(BLANK_UNIT); setAdding(false); load();
  }

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return units || [];
    return (units || []).filter((u) => [u.asset_tag, u.make, u.model, u.device_id, u.lease && u.lease.client_name].some((x) => String(x || '').toLowerCase().includes(s)));
  }, [units, q]);
  const counts = useMemo(() => {
    const c = { available: 0, out: 0, battery: 0 };
    for (const u of units || []) {
      if (u.lease) c.out++; else if (u.status === 'available') c.available++;
      if (u.has_battery) c.battery++;
    }
    return c;
  }, [units]);

  return (
    <>
      <div className="hq-cards">
        <div className="hq-stat"><div className="hq-k">Available</div><div className="hq-v">{units ? counts.available : '—'}</div></div>
        <div className="hq-stat"><div className="hq-k">On a lease</div><div className="hq-v">{units ? counts.out : '—'}</div></div>
        <div className="hq-stat"><div className="hq-k">With batteries</div><div className="hq-v">{units ? counts.battery : '—'}</div></div>
      </div>
      {canManage && adding && (
        <form className="hq-panel" style={{ marginBottom: 16 }} onSubmit={add}>
          <div className="hq-panel-head"><h2>Add equipment</h2></div>
          <div style={{ padding: 20 }}>
            <UnitForm value={form} onChange={setForm} />
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button className="hq-btn" type="submit" disabled={busy || !form.asset_tag.trim()}>Add to inventory</button>
              <button type="button" className="hq-signout" style={GHOST_BTN} onClick={() => { setAdding(false); setForm(BLANK_UNIT); }}>Cancel</button>
            </div>
          </div>
        </form>
      )}
      <div className="hq-panel">
        <div className="hq-panel-head" style={{ flexWrap: 'wrap', gap: 10 }}>
          <input className="hq-input" placeholder="Search tag, model, device ID, client…" aria-label="Search inventory" value={q} onChange={(e) => setQ(e.target.value)} style={{ flex: '1 1 260px', maxWidth: 360 }} />
          {canManage && !adding && <button type="button" className="hq-btn" onClick={() => setAdding(true)}>Add equipment</button>}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="hq-table" style={{ minWidth: 820 }}>
            <thead><tr><th>Asset</th><th>Model</th><th>Device ID</th><th>Batteries</th><th style={{ textAlign: 'right' }}>Replacement</th><th>Status</th></tr></thead>
            <tbody>
              {shown.map((u) => {
                const st = unitState(u);
                return (
                  <tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/equipment/units/' + u.id)}>
                    <td><Link to={'/equipment/units/' + u.id} style={{ ...LINK, fontWeight: 600 }} onClick={(e) => e.stopPropagation()}>{u.asset_tag}</Link>
                      <div style={MUTED}>{KIND_LABEL[u.kind] || u.kind}</div></td>
                    <td>{makeModel(u.make, u.model) || '—'}{u.os_version && <div style={MUTED}>OS {u.os_version}</div>}</td>
                    <td style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>{u.device_id || '—'}</td>
                    <td><BatteryBadges u={u} /></td>
                    <td style={{ textAlign: 'right' }}>{money(u.replacement_value)}</td>
                    <td><span style={pill(st.tone)}>{st.label}</span>
                      {u.lease && <div style={MUTED}>{u.lease.number} · {u.lease.client_name}</div>}</td>
                  </tr>
                );
              })}
              {units && shown.length === 0 && <tr><td className="hq-empty" colSpan={6}>{units.length ? 'Nothing matches that search.' : 'No equipment yet. Add your first unit.'}</td></tr>}
              {units === null && <tr><td className="hq-empty" colSpan={6}>Loading…</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
const GHOST_BTN = { width: 'auto', padding: '0 14px', marginTop: 0, height: 38 };

function TermsPanel({ canManage, onMsg }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    apiFetch('/api/equipment/settings').then(async (r) => {
      const d = await readJson(r);
      if (r.ok) setS(d.settings); else onMsg(d.error || 'Could not load the terms.');
    }).catch(() => {});
  }, [onMsg]);
  if (!s) return <div className="hq-panel"><div className="hq-empty">Loading…</div></div>;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    const r = await apiFetch('/api/equipment/settings', { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(s) });
    const d = await readJson(r);
    setBusy(false);
    if (r.ok) { setS(d.settings); onMsg('Standard terms saved. New leases start from this version; existing leases keep their own copy.'); }
    else onMsg(d.error || 'Could not save.');
  }
  return (
    <form className="hq-panel" onSubmit={save}>
      <div className="hq-panel-head"><h2>Standard lease terms</h2></div>
      <div style={{ padding: 20, display: 'grid', gap: 18 }}>
        <p style={{ ...MUTED, fontSize: 13, margin: 0 }}>
          Every new lease starts with a copy of these. You can still edit the copy on a draft lease.
          In the terms, <code>{'{service_fee_pct}'}</code> becomes the lease’s event service fee, <code>{'{return_address}'}</code> the return address
          and <code>{'{tax_registrations}'}</code> the list of tax registrations below.
        </p>
        <label><span style={LABEL}>Return address (all equipment comes back here)</span>
          <textarea style={{ ...TEXTAREA, minHeight: 80 }} value={s.return_address} onChange={set('return_address')} disabled={!canManage} maxLength={400} /></label>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ flex: '0 1 220px' }}><span style={LABEL}>Default event service fee (%)</span>
            <input className="hq-input" style={{ width: '100%', boxSizing: 'border-box' }} inputMode="decimal" value={s.default_processing_fee_pct} onChange={set('default_processing_fee_pct')} disabled={!canManage} /></label>
          <label style={{ flex: '1 1 320px' }}><span style={LABEL}>Shipping note</span>
            <input className="hq-input" style={{ width: '100%', boxSizing: 'border-box' }} value={s.shipping_note} onChange={set('shipping_note')} disabled={!canManage} maxLength={500} /></label>
        </div>
        <TaxRegistrations value={s.tax_registrations || []} onChange={(v) => setS({ ...s, tax_registrations: v })} disabled={!canManage} />
        <label><span style={LABEL}>Card processing fees — Square, passed through at cost (one per line)</span>
          <textarea style={{ ...TEXTAREA, minHeight: 180, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }} value={s.fee_schedule_template} onChange={set('fee_schedule_template')} disabled={!canManage} /></label>
        <label><span style={LABEL}>Terms and conditions</span>
          <textarea style={{ ...TEXTAREA, minHeight: 420 }} value={s.terms_template} onChange={set('terms_template')} disabled={!canManage} /></label>
        {canManage && <div><button className="hq-btn" type="submit" disabled={busy}>Save standard terms</button></div>}
      </div>
    </form>
  );
}

// @@LEASE_TERMS_v87_1@@ XYZ's sales-tax registrations: printed on every agreement,
// with one registration statement page per state in the lease PDF.
const BLANK_REG = { state: '', state_name: '', tax_type: 'Sales and Use Tax', permit: '', agency: '' };
function TaxRegistrations({ value, onChange, disabled }) {
  const set = (i, k) => (e) => onChange(value.map((r, j) => (j === i ? { ...r, [k]: k === 'state' ? e.target.value.toUpperCase() : e.target.value } : r)));
  return (
    <div>
      <span style={LABEL}>Sales-tax registrations (XYZ files sales tax for tour stops in these states)</span>
      <div style={{ display: 'grid', gap: 8 }}>
        {value.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input className="hq-input" style={{ flex: '0 0 64px', minWidth: 0 }} placeholder="CA" aria-label="State code" maxLength={2} value={r.state} onChange={set(i, 'state')} disabled={disabled} />
            <input className="hq-input" style={{ flex: '1 1 130px', minWidth: 0 }} placeholder="California" aria-label="State name" value={r.state_name} onChange={set(i, 'state_name')} disabled={disabled} />
            <input className="hq-input" style={{ flex: '1 1 150px', minWidth: 0 }} placeholder="Sales and Use Tax" aria-label="Tax type" value={r.tax_type} onChange={set(i, 'tax_type')} disabled={disabled} />
            <input className="hq-input" style={{ flex: '1 1 150px', minWidth: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }} placeholder="Permit number" aria-label="Permit number" value={r.permit} onChange={set(i, 'permit')} disabled={disabled} />
            <input className="hq-input" style={{ flex: '2 1 240px', minWidth: 0 }} placeholder="Issuing agency" aria-label="Issuing agency" value={r.agency} onChange={set(i, 'agency')} disabled={disabled} />
            {!disabled && <button type="button" className="hq-signout" style={GHOST_BTN} onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label={'Remove ' + (r.state || 'row')}>Remove</button>}
          </div>
        ))}
        {value.length === 0 && <div style={MUTED}>None — the agreement will say XYZ files sales tax in no state.</div>}
        {!disabled && <div><button type="button" className="hq-signout" style={GHOST_BTN} onClick={() => onChange([...value, { ...BLANK_REG }])}>Add a state</button></div>}
      </div>
    </div>
  );
}
